import { useCallback, useEffect, useRef, useState } from "react";
import { Camera, Trash2, FileImage, RotateCw, X, FileDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { PageHeader } from "@/components/PageHeader";
import { useAuth } from "@/App";
import { toast } from "sonner";
import { todayISO, fmtDay, uploadHaccpPhoto, photoSrc } from "@/utils/haccp";

let photoSeq = 0;

export default function HaccpDocuments() {
  const { authAxios } = useAuth();
  const fileRef = useRef(null);
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: "", date: todayISO(), note: "" });
  const [photos, setPhotos] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await authAxios.get("/haccp/documents");
      setItems(r.data.items || []);
    } catch { toast.error("Errore caricamento schede"); }
  }, [authAxios]);

  useEffect(() => { load(); }, [load]);

  const pickPhotos = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setPhotos((prev) => [
      ...prev,
      ...files.map((f) => ({ id: `p${++photoSeq}`, file: f, rotation: 0, url: URL.createObjectURL(f) })),
    ]);
    if (e.target) e.target.value = "";
  };

  const rotate = (id) => {
    setPhotos((prev) => prev.map((p) => (p.id === id ? { ...p, rotation: (p.rotation + 90) % 360 } : p)));
  };

  const removePhoto = (id) => {
    setPhotos((prev) => prev.filter((p) => p.id !== id));
  };

  const downloadPdf = async (doc) => {
    const t = toast.loading("Creazione PDF...");
    try {
      const r = await authAxios.get(`/haccp/documents/${doc.id}/pdf`, { responseType: "blob", timeout: 120000 });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `scheda_${doc.date}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`PDF pronto (${Math.round(r.data.size / 1024)} KB)`, { id: t });
    } catch {
      toast.error("Errore creazione PDF", { id: t });
    }
  };

  const save = async () => {
    if (!photos.length) { toast.error("Allega almeno una foto della scheda"); return; }
    setSaving(true);
    const t = toast.loading("Caricamento foto...");
    try {
      const urls = [];
      for (const p of photos) {
        urls.push(await uploadHaccpPhoto(authAxios, p.file, p.rotation));
      }
      await authAxios.post("/haccp/documents", {
        ...form,
        title: form.title.trim() || "Scheda di produzione",
        photo_urls: urls,
      });
      toast.success("Scheda archiviata", { id: t });
      setForm({ title: "", date: todayISO(), note: "" });
      setPhotos([]);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Errore salvataggio", { id: t });
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare questa scheda archiviata?")) return;
    try { await authAxios.delete(`/haccp/documents/${id}`); await load(); }
    catch { toast.error("Errore eliminazione"); }
  };

  return (
    <div className="min-h-screen bg-[#09090b]" data-testid="haccp-documents-page">
      <main className="p-4 sm:p-6 lg:p-8 max-w-3xl mx-auto pb-16">
        <PageHeader title="Schede di produzione" />

        <Card className="bg-[#18181b] border-violet-500/20 mb-5">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center gap-2">
              <FileImage className="w-4 h-4 text-violet-400" />
              <h3 className="text-white text-base font-semibold">Archivia una scheda</h3>
            </div>
            <p className="text-xs text-zinc-500">Fotografa la scheda cartacea: resta archiviata e finisce nel registro PDF del periodo. Le foto vengono compresse sul telefono, quindi anche gli scatti pesanti si caricano in pochi secondi.</p>
            <div className="grid grid-cols-2 gap-2">
              <Input
                data-testid="doc-title-input"
                value={form.title}
                placeholder="Titolo (es. Produzione ravioli)"
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                className="h-10 bg-[#0f0f11] border-white/10 text-white"
              />
              <Input
                type="date"
                data-testid="doc-date-input"
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                className="h-10 bg-[#0f0f11] border-white/10 text-white"
              />
            </div>
            <Textarea
              data-testid="doc-note-input"
              value={form.note}
              placeholder="Note (opzionale)"
              onChange={(e) => setForm({ ...form, note: e.target.value })}
              className="bg-[#0f0f11] border-white/10 text-white min-h-[60px]"
            />
            <input ref={fileRef} type="file" accept="image/*" capture="environment" multiple onChange={pickPhotos} className="hidden" data-testid="doc-photo-input" />
            <Button
              variant="outline"
              data-testid="doc-photo-btn"
              onClick={() => fileRef.current?.click()}
              className="w-full h-10 border-white/10 text-zinc-300 hover:text-amber-400"
            >
              <Camera className="w-4 h-4 mr-1" /> Fotografa / allega ({photos.length})
            </Button>
            {!!photos.length && (
              <div className="flex gap-3 flex-wrap" data-testid="doc-photo-previews">
                {photos.map((p) => (
                  <div key={p.id} className="relative">
                    <img
                      src={p.url}
                      alt="scheda"
                      style={{ transform: `rotate(${p.rotation}deg)` }}
                      className="w-20 h-20 rounded object-cover border border-white/10 transition-transform"
                    />
                    <button
                      type="button"
                      data-testid={`doc-photo-rotate-${p.id}`}
                      onClick={() => rotate(p.id)}
                      className="absolute -bottom-2 -left-2 w-7 h-7 rounded-full bg-zinc-800 border border-white/20 flex items-center justify-center text-zinc-300 hover:text-amber-400"
                      title="Ruota"
                    >
                      <RotateCw className="w-3.5 h-3.5" />
                    </button>
                    <button
                      type="button"
                      data-testid={`doc-photo-remove-${p.id}`}
                      onClick={() => removePhoto(p.id)}
                      className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-zinc-800 border border-white/20 flex items-center justify-center text-zinc-300 hover:text-red-400"
                      title="Rimuovi"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <Button onClick={save} disabled={saving} data-testid="doc-submit-btn" className="w-full h-10 bg-amber-500 hover:bg-amber-400 text-black">
              Archivia scheda
            </Button>
          </CardContent>
        </Card>

        <h3 className="text-white text-base font-semibold mb-3">Archivio</h3>
        <div className="space-y-3" data-testid="doc-list">
          {items.map((d) => (
            <Card key={d.id} className="bg-[#18181b] border-white/10">
              <CardContent className="p-4">
                <div className="flex items-start gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white font-medium truncate">{d.title}</p>
                    <p className="text-[11px] text-zinc-500">{fmtDay(d.date)} · {d.signed_by?.name || "—"}</p>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => downloadPdf(d)} data-testid={`doc-pdf-${d.id}`} className="h-8 text-zinc-400 hover:text-amber-400">
                    <FileDown className="w-4 h-4 mr-1" /> PDF
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(d.id)} data-testid={`doc-delete-${d.id}`} className="h-8 text-zinc-500 hover:text-red-400">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
                {d.note && <p className="text-xs text-zinc-400 mt-1">{d.note}</p>}
                <div className="flex gap-2 flex-wrap mt-2">
                  {(d.photo_urls || []).map((u) => (
                    <a key={u} href={photoSrc(u)} target="_blank" rel="noreferrer">
                      <img src={photoSrc(u)} alt="scheda" className="w-20 h-20 rounded object-cover border border-white/10" />
                    </a>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
          {!items.length && <p className="text-zinc-500 text-sm">Nessuna scheda archiviata.</p>}
        </div>
      </main>
    </div>
  );
}
